import { useEffect, useState, useCallback } from "react";

export type Theme = "light" | "dark";
export type Accent = "blue" | "coral" | "orange";
export type Density = "comfortable" | "compact";

export interface Tweaks {
  theme: Theme;
  accent: Accent;
  density: Density;
}

export const DEFAULT_TWEAKS: Tweaks = {
  theme: "light",
  accent: "blue",
  density: "comfortable",
};

const STORAGE_KEY = "stahlia.tweaks";

export function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TWEAKS;
    const parsed = JSON.parse(raw);
    return {
      theme: parsed.theme === "dark" ? "dark" : "light",
      accent:
        parsed.accent === "coral"
          ? "coral"
          : parsed.accent === "orange"
            ? "orange"
            : "blue",
      density: parsed.density === "compact" ? "compact" : "comfortable",
    };
  } catch {
    return DEFAULT_TWEAKS;
  }
}

export function saveTweaks(tweaks: Tweaks): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
}

export function applyTweaks(tweaks: Tweaks): void {
  const root = document.documentElement;
  root.dataset.theme = tweaks.theme;
  root.dataset.accent = tweaks.accent;
  root.dataset.density = tweaks.density;
  root.classList.toggle("dark", tweaks.theme === "dark");
}

export function useStahliaTweaks() {
  const [tweaks, setTweaks] = useState<Tweaks>(() => loadTweaks());

  useEffect(() => {
    applyTweaks(tweaks);
    saveTweaks(tweaks);
  }, [tweaks]);

  const update = useCallback((patch: Partial<Tweaks>) => {
    setTweaks((prev) => ({ ...prev, ...patch }));
  }, []);

  return { tweaks, update };
}
